import Shimmer from '@/components/ui/Shimmer';
import classnames from 'classnames';
import { memo } from 'react';
import { PageLoadingVariant } from './pageLoadingVariants';

interface PageLoadingProps {
  show?: boolean;
  variant?: PageLoadingVariant;
}

interface RepeatedShimmersProps {
  className: string;
  count: number;
  prefix: string;
}

const RepeatedShimmers: React.FC<RepeatedShimmersProps> = ({ className, count, prefix }) => {
  return Array.from({ length: count }, (_, index) => (
    <Shimmer className={className} key={`${prefix}-${index}`} />
  ));
};

const GlobalLoadingShell: React.FC<
  React.PropsWithChildren<{ useLoggedInHomeFooterHeight?: boolean }>
> = ({ children, useLoggedInHomeFooterHeight = false }) => {
  return (
    <div className="min-h-screen bg-white">
      <div
        className="flex h-24 items-center justify-center bg-gray-lightest sm:h-10"
        aria-label="Global banner loading"
      >
        <div className="container">
          <Shimmer className="h-4 bg-gray-light" />
        </div>
      </div>

      <header className="h-40 border-b border-gray-light bg-white lg:h-24">
        <div className="container flex h-24 items-center gap-5 lg:h-full">
          <div className="size-10 lg:hidden">
            <Shimmer className="size-10 rounded-md" />
          </div>
          <img className="h-6 md:h-8" src="/logo-red.svg" width="64" height="32" alt="PLI Logo" />
          <div className="hidden items-center gap-6 lg:flex">
            <RepeatedShimmers className="h-5 w-20" count={4} prefix="navigation" />
          </div>
          <Shimmer className="ml-auto size-10 rounded-full" />
          <Shimmer className="hidden h-5 w-20 lg:block" />
        </div>
        <div className="container lg:hidden">
          <Shimmer className="h-12 w-full rounded-full" />
        </div>
      </header>

      <main id="main" aria-busy="true" aria-label="Page content loading">
        {children}
      </main>

      <footer
        className={classnames('bg-charcoal py-16', {
          'min-h-[646px] lg:py-20': useLoggedInHomeFooterHeight,
          'min-h-96': !useLoggedInHomeFooterHeight
        })}
        aria-hidden="true"
      >
        <div className="container grid gap-12 md:grid-cols-2">
          <div className="space-y-5">
            <Shimmer className="h-10 w-20 bg-gray-dark" />
            <Shimmer className="h-5 w-full max-w-sm bg-gray-dark" />
            <Shimmer className="h-5 w-3/4 max-w-sm bg-gray-dark" />
          </div>
          <div className="grid grid-cols-2 gap-6">
            <RepeatedShimmers className="h-5 w-full bg-gray-dark" count={6} prefix="footer" />
          </div>
        </div>
      </footer>
    </div>
  );
};

const LoadingPageTitle: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  return (
    <section className={classnames('container', compact ? 'py-10 md:py-12' : 'py-12 md:py-16')}>
      <Shimmer className="mb-6 h-5 w-40" />
      <Shimmer className="h-12 w-5/6 md:h-16 md:w-1/2" />
      <Shimmer className="mt-5 h-6 w-full max-w-2xl" />
      <Shimmer className="mt-3 h-6 w-4/5 max-w-xl" />
    </section>
  );
};

const LoadingCardRow: React.FC<{ compact?: boolean; product?: boolean }> = ({
  compact = false,
  product = false
}) => {
  return (
    <div
      className="flex gap-6 overflow-hidden"
      data-loading-card-row={product ? 'product' : undefined}
    >
      <RepeatedShimmers
        className={classnames(
          'min-w-[75%] rounded-lg',
          product
            ? 'h-[400px] sm:min-w-[37.5%] md:min-w-[calc(25%-18px)]'
            : 'sm:min-w-[45%] md:min-w-[calc(33.333%-16px)] lg:min-w-[calc(25%-18px)]',
          !product && (compact ? 'h-64' : 'h-96')
        )}
        count={4}
        prefix={compact ? 'compact-card' : 'card'}
      />
    </div>
  );
};

const HomepageLoading: React.FC<{ authenticated: boolean }> = ({ authenticated }) => {
  if (authenticated) {
    return (
      <>
        <section
          className="relative mt-[-161px] md:mt-[-95px] lg:mt-[-95px]"
          data-home-loading-section="hero"
        >
          <Shimmer className="min-h-[600px] w-full sm:min-h-[809px] lg:min-h-[550px]" />
          <div className="absolute bottom-6 right-0 z-1 w-1/2 lg:w-full" data-home-loading-controls>
            <div className="container flex items-center justify-end gap-4 lg:justify-start">
              <Shimmer className="size-8 rounded-full" />
              <Shimmer className="h-3 w-20" />
            </div>
          </div>
        </section>

        <section
          className="module-spacing w-full overflow-x-clip"
          data-home-loading-section="library"
        >
          <div className="container flex flex-col gap-8">
            <Shimmer className="h-10 w-48" />
            <div className="flex gap-3">
              <RepeatedShimmers className="h-14 w-40 rounded-full" count={3} prefix="library-tab" />
            </div>
            <LoadingCardRow product />
          </div>
        </section>

        <section
          className="container module-spacing overflow-x-clip"
          data-home-loading-section="credit-plan"
        >
          <Shimmer className="mb-4 h-10 w-2/3 max-w-lg" />
          <Shimmer className="mb-8 h-5 w-full max-w-2xl" />
          <div className="flex gap-6 overflow-hidden" data-credit-plan-loading-cards>
            <RepeatedShimmers
              className="aspect-[0.67] w-[260px] shrink-0 rounded-lg sm728:w-[410px] 2xl:w-[490px]"
              count={4}
              prefix="credit-plan-card"
            />
          </div>
        </section>

        <section className="container module-spacing" data-home-loading-section="onboarding">
          <Shimmer className="mb-8 h-10 w-2/3 max-w-lg" />
          <div data-home-loading-content>
            <Shimmer className="h-64 w-full rounded-lg" />
          </div>
        </section>

        <section className="container module-spacing" data-home-loading-section="compliance">
          <Shimmer className="mb-8 h-10 w-2/3 max-w-lg" />
          <div className="grid gap-6 md:grid-cols-2" data-home-loading-content>
            <RepeatedShimmers
              className="h-[440px] w-full rounded-lg"
              count={2}
              prefix="compliance"
            />
          </div>
        </section>

        <section
          className="module-spacing w-full overflow-x-clip"
          data-home-loading-section="new-on-demand"
        >
          <div className="container flex flex-col gap-8">
            <Shimmer className="h-10 w-48" />
            <div className="flex gap-3 overflow-hidden">
              <RepeatedShimmers
                className="h-14 min-w-40 rounded-full"
                count={4}
                prefix="new-on-demand-tab"
              />
            </div>
            <LoadingCardRow product />
          </div>
        </section>

        <section
          className="module-spacing w-full overflow-x-clip"
          data-home-loading-section="top-ten"
        >
          <div className="container flex flex-col gap-8">
            <Shimmer className="h-10 w-48" />
            <div className="flex gap-3 overflow-hidden">
              <RepeatedShimmers
                className="h-14 min-w-40 rounded-full"
                count={4}
                prefix="top-ten-tab"
              />
            </div>
            <LoadingCardRow product />
          </div>
        </section>

        <section className="bg-gray-lightest" data-home-loading-section="initiatives">
          <div className="container module-spacing">
            <Shimmer className="h-10 w-48" />
            <div
              className="my-6 grid gap-4 sm:my-8 lg:grid-cols-2 lg:gap-6"
              data-home-loading-content
            >
              <RepeatedShimmers
                className="h-[480px] w-full rounded-lg sm:aspect-[2/1] sm:h-auto"
                count={2}
                prefix="initiative"
              />
            </div>
          </div>
        </section>

        <section
          className="container module-spacing overflow-x-clip"
          data-home-loading-section="pli-press"
        >
          <div data-home-loading-content>
            <Shimmer className="h-[500px] w-full rounded-lg" />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <LoadingPageTitle />
      <section className="container pb-16">
        <Shimmer className="h-[37rem] w-full rounded-lg md:h-[51rem] lg:h-[34rem]" />
        <div className="mt-5 flex items-center gap-3">
          <Shimmer className="size-8 rounded-full" />
          <Shimmer className="h-3 w-20" />
        </div>
      </section>
      <section className="container pb-16">
        <Shimmer className="mb-8 h-10 w-2/3 max-w-lg" />
        <LoadingCardRow />
      </section>
    </>
  );
};

const BrowseLoading = () => {
  return (
    <section className="container min-h-screen pb-16">
      <div className="pb-14 pt-16">
        <Shimmer className="h-14 w-52" />
      </div>
      <div className="flex gap-6 overflow-hidden border-b border-gray-light">
        <RepeatedShimmers className="mb-4 h-5 min-w-24" count={5} prefix="browse-tab" />
      </div>
      <div className="my-16">
        <div className="mb-8 flex items-center justify-between gap-8">
          <Shimmer className="h-6 w-44" />
          <Shimmer className="h-5 w-24" />
        </div>
        <div className="space-y-2">
          <RepeatedShimmers className="h-12 w-full rounded-lg" count={24} prefix="browse-row" />
        </div>
      </div>
    </section>
  );
};

const TaxonomyLoading: React.FC<{ variant: 'practice-area' | 'industry' }> = ({ variant }) => {
  return (
    <>
      <LoadingPageTitle compact />
      <section className="container pb-16">
        <div className="mb-10 flex flex-wrap gap-3">
          <RepeatedShimmers
            className="h-12 w-36 rounded-full"
            count={4}
            prefix={`${variant}-tag`}
          />
        </div>
        <LoadingCardRow compact />
      </section>
      <section className="bg-gray-lightest py-16">
        <div className="container grid gap-8 md:grid-cols-2">
          <RepeatedShimmers
            className="h-72 w-full rounded-lg"
            count={2}
            prefix={`${variant}-feature`}
          />
        </div>
      </section>
    </>
  );
};

const CreditLoading: React.FC<{ jurisdiction?: boolean }> = ({ jurisdiction = false }) => {
  return (
    <>
      <LoadingPageTitle compact />
      <section className="container min-h-screen pb-16">
        <div className="grid gap-8 lg:grid-cols-[18rem_1fr]">
          <aside className="space-y-4">
            <Shimmer className="h-10 w-3/4" />
            <RepeatedShimmers className="h-14 w-full" count={5} prefix="credit-link" />
          </aside>
          <div className="space-y-6">
            {jurisdiction && <Shimmer className="h-24 w-full rounded-lg" />}
            <Shimmer className="h-12 w-2/3" />
            <RepeatedShimmers
              className="h-24 w-full rounded-lg"
              count={5}
              prefix="credit-content"
            />
          </div>
        </div>
      </section>
    </>
  );
};

const GenericLoading = () => {
  return (
    <>
      <LoadingPageTitle />
      <section className="container pb-16">
        <Shimmer className="h-80 w-full rounded-lg md:h-96" />
      </section>
    </>
  );
};

const PageLoadingContent: React.FC<{ variant: PageLoadingVariant }> = ({ variant }) => {
  switch (variant) {
    case 'browse':
      return <BrowseLoading />;
    case 'home-logged-in':
      return <HomepageLoading authenticated />;
    case 'home-logged-out':
      return <HomepageLoading authenticated={false} />;
    case 'practice-area':
    case 'industry':
      return <TaxonomyLoading variant={variant} />;
    case 'credit':
    case 'credit-information':
      return <CreditLoading />;
    case 'jurisdiction':
      return <CreditLoading jurisdiction />;
    default:
      return <GenericLoading />;
  }
};

const PageLoadingBase = ({
  show = true,
  variant = 'generic'
}: PageLoadingProps): JSX.Element | null => {
  if (!show) return null;

  return (
    <div data-page-loading-variant={variant}>
      <GlobalLoadingShell useLoggedInHomeFooterHeight={variant === 'home-logged-in'}>
        <PageLoadingContent variant={variant} />
      </GlobalLoadingShell>
    </div>
  );
};

const PageLoading = memo(PageLoadingBase);

PageLoading.displayName = 'PageLoading';
export default PageLoading;
